import { Timestamp } from "firebase/firestore/lite";
import { DbBusiness } from "../business/models";
import { businessService } from "../business/service";
import { clientService } from "../client/service";
import { DbBusinessMemberRole } from "../member/models";
import { memberService } from "../member/service";
import { membershipService } from "../membership/service";
import { useUser } from "../user/user-provider";
import { waitlistService } from "../waitlist/service";
import React, { createContext, useContext, useState, useEffect } from "react";
import { setCookie, getCookie } from "cookies-next";
import { businessSubscriptionService } from "../business/subscription-service";
import { DbBusinessSubscriptionType } from "../business/models";
import { bookingAnalyticsService } from "../booking/analytics-service";

interface BusinessContextProps {
  loadingBusinesses: boolean;
  setLoadingBusinesses: React.Dispatch<React.SetStateAction<boolean>>;
  businesses: DbBusiness[];
  setBusinesses: React.Dispatch<React.SetStateAction<DbBusiness[]>>;
  selectedBusiness: DbBusiness;
  setSelectedBusiness: React.Dispatch<React.SetStateAction<DbBusiness>>;
  selectedBusinessMemberships: number;
  setSelectedBusinessMemberships: React.Dispatch<React.SetStateAction<number>>;
  selectedBusinessWaitlistBookings: number;
  setSelectedBusinessWaitlistBookings: React.Dispatch<
    React.SetStateAction<number>
  >;
  selectedBusinessClients: number;
  setSelectedBusinessClients: React.Dispatch<React.SetStateAction<number>>;
  selectedBusinessBookings: number;
  setSelectedBusinessBookings: React.Dispatch<React.SetStateAction<number>>;
  userRole: DbBusinessMemberRole | null;
  isSubscriptionActive: boolean;
  fetchSelectedBusinessDetails: (business: DbBusiness) => void;
}

const BusinessContext = createContext<BusinessContextProps | null>(null);

export default function BusinessProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useUser();
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);
  const [businesses, setBusinesses] = useState<DbBusiness[]>([]);
  const [userRole, setUserRole] = useState<DbBusinessMemberRole | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<DbBusiness>(
    businesses[0],
  );

  const [selectedBusinessMemberships, setSelectedBusinessMemberships] =
    useState<number>(0);
  const [
    selectedBusinessWaitlistBookings,
    setSelectedBusinessWaitlistBookings,
  ] = useState<number>(0);
  const [selectedBusinessClients, setSelectedBusinessClients] =
    useState<number>(0);
  const [selectedBusinessBookings, setSelectedBusinessBookings] =
    useState<number>(0);
  const [isSubscriptionActive, setIsSubscriptionActive] =
    useState<boolean>(true);

  useEffect(() => {
    // Fetch required details for selected business
    if (
      !selectedBusiness ||
      !user ||
      businessService.isEmptyBusiness(selectedBusiness)
    ) {
      return;
    } else {
      fetchSelectedBusinessDetails(selectedBusiness);
      setCookie("selection", selectedBusiness.id);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const fetchBusinesses = async () => {
      const businesses = await businessService.getBusinessesByUserId(
        user?.id || "",
      );
      const filteredBusinesses = businesses.filter(
        (business) => !business.is_deleted,
      );
      setBusinesses(filteredBusinesses);
      if (
        (filteredBusinesses.length > 0 && !selectedBusiness) ||
        businessService.isEmptyBusiness(selectedBusiness)
      ) {
        const business = filteredBusinesses.find(
          (b) => b.id === getCookie("selection"),
        );

        if (business) {
          setSelectedBusiness(business);
        } else {
          setSelectedBusiness(businesses[0]);
        }
      }
      if (user?.user_name != "") {
        setLoadingBusinesses(false);
      }
    };

    fetchBusinesses();
  }, [user, selectedBusiness]);

  const fetchSelectedBusinessDetails = async (business: DbBusiness) => {
    if (business.subscription) {
      if (
        business.subscription.type === DbBusinessSubscriptionType.basic_plan
      ) {
        setIsSubscriptionActive(false);
      } else if (business.subscription.expiry_date) {
        const isExpired =
          Timestamp.now().seconds > business.subscription.expiry_date.seconds;
        setIsSubscriptionActive(!isExpired);
        if (isExpired) {
          await businessSubscriptionService.updateSubscriptionType(
            business.id,
            DbBusinessSubscriptionType.basic_plan,
          );
        }
      }
    }

    const [memberships, waitlistBookings, clients, bookings, memberDetail] =
      await Promise.all([
        membershipService.getBusinessMembershipCounts(business.id),
        waitlistService.getWaitlistBookingCounts(business.id),
        clientService.getClientCounts(business.id, null, null),
        bookingAnalyticsService.getBookingCounts(business.id),
        memberService.getBusinessMember(business.id, user?.id!),
      ]);

    setSelectedBusinessMemberships(memberships);
    setSelectedBusinessWaitlistBookings(waitlistBookings);
    setSelectedBusinessClients(clients);
    setSelectedBusinessBookings(bookings);
    setUserRole(memberDetail.role);
  };

  return (
    <BusinessContext.Provider
      value={{
        loadingBusinesses,
        setLoadingBusinesses,
        businesses,
        setBusinesses,
        selectedBusiness,
        setSelectedBusiness,
        selectedBusinessMemberships,
        setSelectedBusinessMemberships,
        selectedBusinessWaitlistBookings,
        setSelectedBusinessWaitlistBookings,
        selectedBusinessClients,
        setSelectedBusinessClients,
        selectedBusinessBookings,
        setSelectedBusinessBookings,
        userRole,
        isSubscriptionActive,
        fetchSelectedBusinessDetails,
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
}

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error("useBusiness must be used within BusinessProvider");
  }
  return context;
};
